import { HelpIcon } from '@/shared/components/HelpIcon'
import { PushCadenceChart } from '@/shared/components/PushCadenceChart'
import type { TenantAnalytics } from '@/lib/apiTypes'

/**
 * Org-wide push cadence - the volume side of the pair whose other half is apply runtime.
 *
 * 30 days, not the State page's 90: this reads `state_daily_series`, which the analytics
 * rollup computes over THIRTY_DAYS_MS. The window is stated in the heading rather than assumed,
 * because the card beside it ranks applies over a different span.
 *
 * Titled "State push cadence" because a bare "Push cadence" is ambiguous on a dashboard that also
 * trends module publishes and downloads. The series counts rows in `state_versions` — one per
 * state-file version written to the state backend — so this is a state metric only; nothing in it
 * is registry activity. Same domain-naming habit as "Open findings · modules / · state" above.
 */
export function PushCadenceCard({ series }: { series: TenantAnalytics['state_daily_series'] }) {
  return (
    <div className="card fu">
      <div className="card-header">
        <div>
          <div className="kicker">Activity</div>
          <h3 style={{ fontSize: '0.95rem' }}>State push cadence (30 days)</h3>
        </div>
        <HelpIcon track="help_push_cadence" title="State push cadence" kicker="// what this implies">
          <p>Every state push across the org, counted per <strong>UTC day</strong> — one point per
          new <strong>state-file version</strong>, so module publishes and registry downloads are
          not in here. A day nobody applied plots at <strong>zero</strong> rather than being
          skipped, so a dip to the axis is a real gap and the line is never drawn across one.
          Position carries real elapsed time, so the shape is the org's actual rhythm rather than a
          run of events squashed together.</p>
          <p>Read it against <strong>Slowest applies</strong> beside it. Runtime is spend per
          apply, and cadence is how often you pay it - a workspace with a four-minute apply that
          runs twenty times a day costs far more runner time than a ten-minute apply that runs
          weekly.</p>
          <p>Activity on every weekday is healthy automation. A long flat stretch broken by a
          single tall spike usually means changes are being <strong>batched up and released
          together</strong>, which is exactly the pattern that makes an apply risky: more
          resources move at once, so a failure has more blast radius and is harder to attribute.</p>
        </HelpIcon>
      </div>
      <div style={{ padding: '16px 18px' }}>
        <PushCadenceChart pushes={series} windowDays={30} />
      </div>
    </div>
  )
}

